import React, { useState, useEffect } from "react";
import { Plus, Minus, Check, ArrowRight } from "lucide-react";
import Reveal, { Eyebrow } from "../components/common/Reveal";
import Testimonials from "../components/home/Testimonials";
import { careTeam, BOOK_URL } from "../mock";
import { track } from "../lib/tracking";

export default function ProgramPage({ data }) {
  const [open, setOpen] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    track("view_content", { custom_data: { content_name: data.eyebrow, content_category: data.slug } });
  }, [data.slug, data.eyebrow]);

  return (
    <main>
      {/* Hero */}
      <section className="relative pt-28 lg:pt-36 pb-16 lg:pb-24 bg-gradient-to-b from-lybl-sand to-lybl-paper">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="reveal">
            <Eyebrow>{data.eyebrow}</Eyebrow>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] text-lybl-ink text-balance">{data.title}</h1>
            <p className="mt-6 text-lg text-lybl-muted max-w-lg">{data.subtitle}</p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href={BOOK_URL} target="_blank" rel="noreferrer" data-evt="schedule" className="inline-flex items-center gap-2 rounded-full bg-lybl-green px-7 py-4 text-[15px] font-medium text-white hover:bg-lybl-greenDark transition-colors">Book Free Consultation</a>
              <a href="#how-we-address" className="inline-flex items-center gap-2 rounded-full border border-lybl-ink/20 px-6 py-4 text-[15px] font-medium text-lybl-ink hover:border-lybl-green transition-colors">See How It Works</a>
            </div>
          </div>
          <div className="reveal">
            <div className="overflow-hidden rounded-3xl shadow-xl">
              <img src={data.heroImage} alt={data.eyebrow} className="w-full h-80 lg:h-[26rem] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Quote + hero stats */}
      <section className="bg-lybl-green text-white py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="font-serif text-2xl lg:text-3xl leading-relaxed text-white/95">"{data.quote}"</p>
            <p className="mt-5 text-[13px] font-semibold uppercase tracking-wider text-white/60">{data.quoteWho}</p>
          </Reveal>
          <Reveal delay={120}>
            <div className="grid sm:grid-cols-3 gap-6">
              {data.heroStats.map((s) => (
                <div key={s.text} className="rounded-2xl bg-white/10 p-6">
                  <p className="font-serif text-4xl text-white">{s.value}</p>
                  <p className="mt-2 text-[13px] text-white/75 leading-snug">{s.text}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* What we address - marquee chips */}
      <section className="bg-white py-20 lg:py-28 overflow-hidden">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 text-center">
          <Reveal><Eyebrow>{data.addressEyebrow}</Eyebrow></Reveal>
          <Reveal delay={80}><h2 className="mt-4 font-serif text-3xl lg:text-4xl text-lybl-ink max-w-3xl mx-auto text-balance">{data.addressTitle}</h2></Reveal>
        </div>
        <div className="mt-12 relative">
          <div className="flex gap-3 w-max animate-marquee">
            {[...data.chips, ...data.chips].map((c, i) => (
              <span key={i} className="rounded-full bg-lybl-cream ring-1 ring-lybl-ink/5 px-5 py-2.5 text-[14px] text-lybl-ink/80 whitespace-nowrap">{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Why it matters */}
      <section className="bg-lybl-cream py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <Eyebrow>{data.whyEyebrow}</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl lg:text-4xl text-lybl-ink text-balance">{data.whyTitle}</h2>
            <p className="mt-5 text-lybl-muted text-[15px] leading-relaxed">{data.whyLead}</p>
            <div className="mt-8 overflow-hidden rounded-3xl">
              <img src={data.whyImage} alt="" className="w-full h-64 object-cover" />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <ul className="space-y-4">
              {data.whyPoints.map((p) => (
                <li key={p} className="flex gap-4 rounded-2xl bg-white ring-1 ring-black/5 p-5">
                  <span className="h-6 w-6 shrink-0 rounded-full bg-lybl-green/10 flex items-center justify-center mt-0.5">
                    <Check className="h-3.5 w-3.5 text-lybl-green" />
                  </span>
                  <p className="text-[15px] text-lybl-ink/80 leading-relaxed">{p}</p>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* How we address - care cards */}
      <section id="how-we-address" className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="text-center">
            <Reveal><Eyebrow>How We Address</Eyebrow></Reveal>
            <Reveal delay={80}><h2 className="mt-4 font-serif text-3xl lg:text-4xl text-lybl-ink">An expert-led integrated model of care</h2></Reveal>
            <Reveal delay={140}><p className="mt-4 max-w-2xl mx-auto text-lybl-muted text-lg">A whole-body, root-cause clinical model designed to deliver consistent, evidence-based, and deeply personalized care.</p></Reveal>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {data.careCards.map((c, i) => (
              <Reveal key={c.title} delay={i * 90}>
                <div className="h-full overflow-hidden rounded-3xl bg-lybl-paper ring-1 ring-black/5">
                  <div className="h-44 overflow-hidden"><img src={c.image} alt={c.title} className="h-full w-full object-cover" /></div>
                  <div className="p-6">
                    <span className="text-[12px] font-semibold uppercase tracking-wider text-lybl-rust">{c.tag}</span>
                    <h3 className="mt-2 font-serif text-xl text-lybl-ink">{c.title}</h3>
                    <p className="mt-3 text-[14px] text-lybl-muted leading-relaxed">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Clinical outcomes */}
      <section className="bg-lybl-lavender/40 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="text-center">
            <Reveal><Eyebrow color="blue">{data.outcomesEyebrow}</Eyebrow></Reveal>
            <Reveal delay={80}><h2 className="mt-4 font-serif text-3xl lg:text-4xl text-lybl-ink">{data.outcomesTitle}</h2></Reveal>
            <Reveal delay={140}><p className="mt-4 text-lybl-muted text-lg">{data.outcomesSubtitle}</p></Reveal>
          </div>
          <div className={`mt-12 grid gap-6 ${data.outcomes.length > 1 ? "md:grid-cols-2" : "max-w-2xl mx-auto"}`}>
            {data.outcomes.map((o) => (
              <Reveal key={o.heading}>
                <div className="rounded-3xl bg-white ring-1 ring-black/5 p-7">
                  <h3 className="font-serif text-2xl text-lybl-green">{o.heading}</h3>
                  <ul className="mt-5 space-y-3">
                    {o.items.map((it) => (
                      <li key={it} className="flex items-center gap-3 text-[15px] text-lybl-ink/80">
                        <span className="h-5 w-5 rounded-full bg-lybl-green flex items-center justify-center shrink-0"><Check className="h-3 w-3 text-white" /></span>
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Care journey */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="text-center">
            <Reveal><Eyebrow>The Program</Eyebrow></Reveal>
            <Reveal delay={80}><h2 className="mt-4 font-serif text-3xl lg:text-4xl text-lybl-ink">Your Care Journey</h2></Reveal>
            <Reveal delay={140}><p className="mt-4 text-lybl-muted text-lg">Five steps, one cohesive plan from first conversation to lasting results.</p></Reveal>
          </div>
          <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-5">
            {data.journeySteps.map((s, i) => (
              <Reveal key={s.n} delay={i * 70}>
                <div className="h-full rounded-2xl border border-lybl-ink/10 p-6 hover:border-lybl-green/30 hover:bg-lybl-cream/40 transition-colors">
                  <span className="font-serif text-3xl text-lybl-rust">{s.n}</span>
                  <p className="mt-2 text-[12px] font-semibold uppercase tracking-wide text-lybl-green">{s.tag}</p>
                  <h3 className="mt-2 font-serif text-lg text-lybl-ink">{s.title}</h3>
                  <p className="mt-2 text-[13px] text-lybl-muted leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-lybl-cream py-20 lg:py-28">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <Reveal><Eyebrow>Get Started</Eyebrow></Reveal>
          <Reveal delay={80}><h2 className="mt-4 font-serif text-3xl lg:text-4xl text-lybl-ink">Complete care, transparent pricing</h2></Reveal>
          <Reveal delay={140}><p className="mt-4 text-lybl-muted text-lg">Start with a free consultation, then choose the plan that fits you. Each includes consults, supplements, diet and lifestyle guidance, yoga therapy, and shipping.</p></Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 text-left">
            <Reveal>
              <div className="relative rounded-3xl bg-lybl-green text-white p-8 h-full">
                <span className="absolute -top-3 left-8 rounded-full bg-lybl-rust px-3 py-1 text-[12px] font-semibold">Most Popular</span>
                <p className="font-serif text-4xl">$140<span className="text-lg text-white/70">/month</span></p>
                <p className="mt-2 text-white/80">Ongoing care | Cancel anytime</p>
                <p className="mt-4 text-[14px] text-white/70">Recommended for best results.</p>
                <a href={BOOK_URL} target="_blank" rel="noreferrer" data-evt="schedule" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-[15px] font-medium text-lybl-green hover:bg-lybl-cream transition-colors">Get Started <ArrowRight className="h-4 w-4" /></a>
              </div>
            </Reveal>
            <Reveal delay={90}>
              <div className="rounded-3xl bg-white ring-1 ring-black/5 p-8 h-full">
                <p className="font-serif text-4xl text-lybl-ink">$170<span className="text-lg text-lybl-muted"> one-time</span></p>
                <p className="mt-2 text-lybl-muted">Full care for one month</p>
                <p className="mt-4 text-[14px] text-lybl-muted">Switch to monthly anytime.</p>
                <a href={BOOK_URL} target="_blank" rel="noreferrer" data-evt="schedule" className="mt-6 inline-flex items-center gap-2 rounded-full border border-lybl-green/30 px-6 py-3 text-[15px] font-medium text-lybl-green hover:bg-lybl-green hover:text-white transition-colors">Get Started <ArrowRight className="h-4 w-4" /></a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* Care team */}
      <section id="team" className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="text-center">
            <Reveal><Eyebrow>Integrated Team</Eyebrow></Reveal>
            <Reveal delay={80}><h2 className="mt-4 font-serif text-3xl lg:text-4xl text-lybl-ink">Meet Your Care Team</h2></Reveal>
            <Reveal delay={140}><p className="mt-4 max-w-3xl mx-auto text-lybl-muted text-lg">Led by board-certified MDs, pharmacologists, licensed Ayurvedic physicians, certified yoga therapists, and holistic wellness experts.</p></Reveal>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {careTeam.map((m, i) => (
              <Reveal key={m.name} delay={i * 70}>
                <div className="text-center">
                  <div className="overflow-hidden rounded-3xl aspect-[3/4]">
                    <img src={m.image} alt={m.name} className="h-full w-full object-cover" />
                  </div>
                  <h3 className="mt-4 font-serif text-lg text-lybl-ink">{m.name}</h3>
                  <p className="text-[13px] font-medium text-lybl-rust">{m.role}</p>
                  <p className="mt-2 text-[12px] text-lybl-muted leading-snug">{m.bio}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-lybl-paper py-20 lg:py-28">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <div className="text-center">
            <Reveal><Eyebrow>How Can We Help</Eyebrow></Reveal>
            <Reveal delay={80}><h2 className="mt-4 font-serif text-3xl lg:text-4xl text-lybl-ink">Frequently Asked Questions</h2></Reveal>
          </div>
          <div className="mt-10 space-y-3">
            {data.faqs.map((it, i) => (
              <div key={it.q} className="rounded-2xl bg-white ring-1 ring-black/5 overflow-hidden">
                <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left">
                  <span className="font-serif text-lg text-lybl-ink">{it.q}</span>
                  <span className="h-8 w-8 shrink-0 rounded-full bg-lybl-cream flex items-center justify-center text-lybl-green">
                    {open === i ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  </span>
                </button>
                <div className="grid transition-all duration-300" style={{ gridTemplateRows: open === i ? "1fr" : "0fr" }}>
                  <div className="overflow-hidden"><p className="px-6 pb-5 text-[15px] text-lybl-muted leading-relaxed">{it.a}</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-lybl-green py-20 lg:py-24">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 text-center">
          <Reveal><h2 className="font-serif text-3xl lg:text-4xl text-white text-balance">{data.finalTitle}</h2></Reveal>
          <Reveal delay={80}><p className="mt-5 text-white/75 text-lg">Start with a free 60-minute consultation to discuss your symptoms, goals, and whether our program is right for you.</p></Reveal>
          <Reveal delay={140}>
            <a href={BOOK_URL} target="_blank" rel="noreferrer" data-evt="schedule" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-4 text-[15px] font-medium text-lybl-green hover:bg-lybl-cream transition-colors">Schedule Your Consult <ArrowRight className="h-4 w-4" /></a>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
